import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Stack,
  Box,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import {
  X,
  Copy,
  Check,
  Eye,
  RefreshCw,
  ArrowDownCircle,
} from 'lucide-react';
import { rabbitmqApi } from '../../../../services/rabbitmqServices/rabbitmqService';

export const PeekMessagesModal = ({
  isOpen = false,
  onClose = () => {},
  queueName = '',
  vhost = '/',
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [count, setCount] = useState(10);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchMessages = async (fetchCount = count) => {
    setLoading(true);
    try {
      const data = await rabbitmqApi.peekMessages({ vhost, name: queueName, count: fetchCount });
      setMessages(Array.isArray(data) ? data : []);
      setHasFetched(true);
    } catch (err) {
      console.error('Failed to peek messages:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      fetchMessages(count);
    }
  }, [isOpen, queueName]);

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Eye style={{ width: 22, height: 22, color: '#006951' }} />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Get Messages ({queueName})
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Safe preview with requeue — messages remain safely in the queue
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <X style={{ width: 18, height: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* Control Bar */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="count-select-label">Fetch Count</InputLabel>
              <Select
                labelId="count-select-label"
                label="Fetch Count"
                value={count}
                onChange={(e) => {
                  const newCount = Number(e.target.value);
                  setCount(newCount);
                  fetchMessages(newCount);
                }}
              >
                <MenuItem value={1}>1 message</MenuItem>
                <MenuItem value={5}>5 messages</MenuItem>
                <MenuItem value={10}>10 messages</MenuItem>
                <MenuItem value={20}>20 messages</MenuItem>
                <MenuItem value={50}>50 messages</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Button
            size="small"
            variant="contained"
            onClick={() => fetchMessages(count)}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshCw style={{ width: 14, height: 14 }} />}
            sx={{ bgcolor: '#006951', '&:hover': { bgcolor: '#005441' } }}
          >
            {loading ? 'Fetching...' : hasFetched ? 'Refresh Messages' : 'Get Messages'}
          </Button>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3, maxHeight: '60vh', overflowY: 'auto' }}>
        {!hasFetched ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <ArrowDownCircle style={{ width: 36, height: 36, margin: '0 auto 8px', color: '#94a3b8' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>Ready to peek messages</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Click &apos;Get Messages&apos; to retrieve payload contents without dropping messages.
            </Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No messages currently queued in <strong>{queueName}</strong>.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {messages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                  bgcolor: '#fff',
                }}
              >
                {/* Header Bar */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#006951' }}>
                      #{idx + 1}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Routing Key: <strong style={{ fontFamily: 'monospace' }}>{msg.routing_key || '(none)'}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Exchange: <strong style={{ fontFamily: 'monospace' }}>{msg.exchange || '(direct)'}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                      {msg.payload_bytes} bytes
                    </Typography>
                  </Stack>

                  <Button
                    size="small"
                    startIcon={copiedIdx === idx ? <Check style={{ width: 14, height: 14, color: '#059669' }} /> : <Copy style={{ width: 14, height: 14 }} />}
                    onClick={() => handleCopy(msg.payload, idx)}
                    sx={{ fontSize: '0.7rem', color: copiedIdx === idx ? '#059669' : 'text.secondary' }}
                  >
                    {copiedIdx === idx ? 'Copied' : 'Copy'}
                  </Button>
                </Stack>

                {/* Payload */}
                <Box sx={{ p: 2 }}>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 1.5,
                      bgcolor: '#f1f5f9',
                      border: '1px solid',
                      borderColor: 'divider',
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                      maxHeight: 200,
                      overflow: 'auto',
                    }}
                  >
                    {msg.payload}
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};
